import React, { useEffect, useMemo, useRef, useState } from 'react';
import { doc, getDoc, getFirestore } from 'firebase/firestore';
import { getDownloadURL, getStorage, ref } from 'firebase/storage';
import {
  GAZE_CATEGORY_COLORS,
  classifyGazeEvents,
  classifyGazePoint,
  gazeDensity,
  isNewStyleEval,
  readScreen,
  resolveEvalStoragePaths,
  tileFractions,
} from './gazeHeatmapData';

const imageUrlCache = new Map();
const labelsCache = new Map();

const LEGEND = [
  ['off', 'Off stimulus'],
  ['image', 'On stimulus'],
  ['sexualized', 'Sexualized region'],
  ['face', 'Face'],
];

// The AOI labels the cloud analysis scores against (images/{hash}.labels).
async function loadLabels(hash) {
  if (!hash) return null;
  if (labelsCache.has(hash)) return labelsCache.get(hash);
  const snapshot = await getDoc(doc(getFirestore(), 'images', hash));
  const labels = snapshot.exists() ? snapshot.data().labels ?? null : null;
  labelsCache.set(hash, labels);
  return labels;
}

function validateImageUrl(url) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(url);
    image.onerror = () => reject(new Error('Image failed to load'));
    image.src = url;
  });
}

async function loadImageUrl(storage, image) {
  const cacheKey = image.hash || image.key;
  const cachedUrl = imageUrlCache.get(cacheKey);
  if (cachedUrl) {
    try {
      return await validateImageUrl(cachedUrl);
    } catch (error) {
      imageUrlCache.delete(cacheKey);
    }
  }

  const url = await getDownloadURL(ref(storage, image.key));
  await validateImageUrl(url);
  imageUrlCache.set(cacheKey, url);
  return url;
}

// Density is shown on a square-root scale so secondary clusters stay visible
// next to the slide's peak; below the floor it stays transparent so a few
// stray samples don't tint the whole slide.
const DENSITY_FLOOR = 0.15;

function heatColor(intensity) {
  if (intensity < DENSITY_FLOOR) return [0, 0, 0, 0];
  if (intensity < 0.35) {
    const t = intensity / 0.35;
    return [0, Math.round(120 + 135 * t), 255 - Math.round(155 * t), Math.round(210 * intensity)];
  }
  if (intensity < 0.7) {
    const t = (intensity - 0.35) / 0.35;
    return [Math.round(255 * t), 255, Math.round(100 * (1 - t)), Math.round(100 + 120 * intensity)];
  }
  const t = (intensity - 0.7) / 0.3;
  return [255, Math.round(255 * (1 - t)), 0, Math.round(150 + 105 * intensity)];
}

function paintGaze(canvas, points, mode) {
  if (!canvas) return;
  const rect = canvas.getBoundingClientRect();
  const width = Math.max(1, Math.round(rect.width));
  const height = Math.max(1, Math.round(rect.height));
  const pixelRatio = window.devicePixelRatio || 1;
  canvas.width = Math.round(width * pixelRatio);
  canvas.height = Math.round(height * pixelRatio);

  const context = canvas.getContext('2d');
  context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
  context.clearRect(0, 0, width, height);

  if (mode === 'points') {
    context.globalAlpha = 0.72;
    points.forEach(({ x, y, category }) => {
      context.fillStyle = GAZE_CATEGORY_COLORS[category] ?? GAZE_CATEGORY_COLORS.off;
      context.beginPath();
      context.arc(x * width, y * height, 2.25, 0, Math.PI * 2);
      context.fill();
    });
    context.globalAlpha = 1;
    return;
  }

  const densityWidth = Math.min(420, width);
  const densityHeight = Math.max(1, Math.round(densityWidth * height / width));
  const densityCanvas = document.createElement('canvas');
  densityCanvas.width = densityWidth;
  densityCanvas.height = densityHeight;
  const densityContext = densityCanvas.getContext('2d');
  const density = gazeDensity(points, densityWidth, densityHeight, Math.max(3, densityWidth * 0.015));
  const pixels = densityContext.createImageData(densityWidth, densityHeight);
  density.forEach((value, index) => {
    pixels.data.set(heatColor(Math.sqrt(value)), index * 4);
  });
  densityContext.putImageData(pixels, 0, 0);
  context.globalAlpha = 0.78;
  context.imageSmoothingEnabled = true;
  context.drawImage(densityCanvas, 0, 0, width, height);
  context.globalAlpha = 1;
}

export default function GazeHeatmapViewer({ evaluation }) {
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [viewerData, setViewerData] = useState(null);
  const [slideImages, setSlideImages] = useState({});
  const [slideLabels, setSlideLabels] = useState({});
  const [mode, setMode] = useState('points');
  const [isLoading, setIsLoading] = useState(true);
  const [imagesLoading, setImagesLoading] = useState(false);
  const [error, setError] = useState(null);
  const [canvasSizeVersion, setCanvasSizeVersion] = useState(0);
  const containerRef = useRef(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchData() {
      setIsLoading(true);
      setError(null);
      setViewerData(null);
      setSlideImages({});
      setCurrentSlideIndex(0);

      try {
        const evalSnapshot = await getDoc(doc(getFirestore(), 'evals', evaluation.id));
        if (!evalSnapshot.exists()) throw new Error('Evaluation metadata not found');

        const paths = resolveEvalStoragePaths(evaluation, evalSnapshot.data());
        const storage = getStorage();
        let rawEval = null;
        let lastError = null;

        for (const path of paths) {
          try {
            const url = await getDownloadURL(ref(storage, path));
            const response = await fetch(url);
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            rawEval = await response.json();
            break;
          } catch (candidateError) {
            lastError = candidateError;
          }
        }

        if (!rawEval) {
          const detail = lastError?.message ? ` (${lastError.message})` : '';
          throw new Error(`Unable to load evaluation JSON. Tried: ${paths.join(', ') || 'no candidate paths'}${detail}`);
        }

        const slides = rawEval.Slides ?? rawEval.slides ?? evaluation.slides ?? [];
        const screen = readScreen(rawEval.Screen ?? rawEval.screen ?? evaluation.screen);
        const gazeEvents = rawEval.GazeEvents ?? rawEval.gazeEvents ?? [];
        if (!Array.isArray(slides) || slides.length === 0) throw new Error('No slide data is available for this evaluation');
        if (!screen) throw new Error('Evaluation screen dimensions are missing or invalid');

        const classified = classifyGazeEvents(gazeEvents, slides.length);
        if (!cancelled) {
          setViewerData({
            slides,
            screen,
            isNewStyle: isNewStyleEval(rawEval),
            ...classified,
          });
        }
      } catch (fetchError) {
        if (!cancelled) setError(fetchError.message);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    if (evaluation?.id) fetchData();
    return () => { cancelled = true; };
  }, [evaluation]);

  const slides = viewerData?.slides ?? [];
  const currentSlide = slides[currentSlideIndex];
  const currentPoints = viewerData?.pointsBySlideIndex[currentSlideIndex] ?? [];
  const invalidCount = viewerData?.invalidCountBySlideIndex[currentSlideIndex] ?? 0;
  const tileSize = useMemo(() => (
    viewerData ? tileFractions(viewerData.screen, viewerData.isNewStyle) : null
  ), [viewerData]);
  const categorizedPoints = useMemo(() => (
    tileSize
      ? currentPoints.map((point) => ({
        ...point,
        category: classifyGazePoint(point, currentSlide?.layouts, tileSize, slideLabels),
      }))
      : currentPoints
  ), [currentPoints, currentSlide, tileSize, slideLabels]);

  useEffect(() => {
    if (!currentSlide) return undefined;
    let cancelled = false;

    async function loadCurrentSlideImages() {
      setImagesLoading(true);
      const storage = getStorage();
      const imagesByKey = new Map((currentSlide.images ?? []).map((image) => [image.key, image]));
      const entries = await Promise.all((currentSlide.layouts ?? []).map(async (layout) => {
        const image = imagesByKey.get(layout.Key);
        if (!image) return [layout.Key, null];
        try {
          return [layout.Key, await loadImageUrl(storage, image)];
        } catch (imageError) {
          console.error(`Error loading image ${image.key}:`, imageError);
          imageUrlCache.delete(image.hash || image.key);
          return [layout.Key, null];
        }
      }));
      if (!cancelled) {
        setSlideImages(Object.fromEntries(entries));
        setImagesLoading(false);
      }
    }

    async function loadCurrentSlideLabels() {
      const entries = await Promise.all((currentSlide.images ?? []).map(async (image) => {
        try {
          return [image.hash, await loadLabels(image.hash)];
        } catch (labelError) {
          // Without labels the point still counts as on-stimulus, as in the analysis.
          console.error(`Error loading labels for ${image.hash}:`, labelError);
          return [image.hash, null];
        }
      }));
      if (!cancelled) setSlideLabels(Object.fromEntries(entries));
    }

    setSlideImages({});
    setSlideLabels({});
    loadCurrentSlideImages();
    loadCurrentSlideLabels();
    return () => { cancelled = true; };
  }, [currentSlide]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;
    const updateCanvasSize = () => setCanvasSizeVersion((version) => version + 1);
    updateCanvasSize();
    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(updateCanvasSize);
      observer.observe(container);
      return () => observer.disconnect();
    }
    window.addEventListener('resize', updateCanvasSize);
    return () => window.removeEventListener('resize', updateCanvasSize);
  }, [viewerData]);

  useEffect(() => {
    paintGaze(canvasRef.current, categorizedPoints, mode);
  }, [categorizedPoints, mode, canvasSizeVersion]);

  if (!evaluation?.id) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-center h-32 text-gray-500">No evaluation data is available, so the gaze heatmap cannot be shown.</div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-center h-32 text-gray-500">Loading evaluation data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-center h-32 text-red-600">Error loading evaluation data: {error}</div>
      </div>
    );
  }

  if (!currentSlide || !tileSize) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-center h-32 text-gray-500">No slide data is available for this evaluation, so the gaze heatmap cannot be shown.</div>
      </div>
    );
  }

  const slideType = currentSlide.type === 0 ? 'Control' : currentSlide.type === 1 ? 'Stimuli' : 'Unknown';

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <div className="flex flex-wrap justify-between items-center gap-4 mb-4">
        <div>
          <h4 className="text-lg font-medium text-gray-900">Gaze Heatmap Viewer</h4>
          <p className="mt-1 text-sm text-gray-600">
            {slideType} · {currentPoints.length.toLocaleString()} valid points · {invalidCount.toLocaleString()} invalid samples
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="inline-flex rounded-md shadow-sm" role="group" aria-label="Gaze display mode">
            {['points', 'heatmap'].map((displayMode) => (
              <button
                key={displayMode}
                type="button"
                onClick={() => setMode(displayMode)}
                className={`px-3 py-2 text-sm font-medium border border-blue-600 first:rounded-l-md last:rounded-r-md ${mode === displayMode ? 'bg-blue-600 text-white' : 'bg-white text-blue-700 hover:bg-blue-50'}`}
              >
                {displayMode === 'heatmap' ? 'Heatmap' : 'Points'}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => setCurrentSlideIndex((index) => Math.max(0, index - 1))}
            disabled={currentSlideIndex === 0}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:text-gray-600 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <span className="text-sm text-gray-600">Slide {currentSlideIndex + 1} of {slides.length}</span>
          <button
            type="button"
            onClick={() => setCurrentSlideIndex((index) => Math.min(slides.length - 1, index + 1))}
            disabled={currentSlideIndex === slides.length - 1}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:text-gray-600 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      </div>

      {mode === 'points' && (
        <div className="flex flex-wrap gap-4 mb-3 text-xs text-gray-500">
          {LEGEND.map(([category, label]) => (
            <span key={category} className="flex items-center gap-1">
              <span className="inline-block w-3 h-3 rounded-full" style={{ backgroundColor: GAZE_CATEGORY_COLORS[category] }} />
              {label}
            </span>
          ))}
        </div>
      )}

      <div
        ref={containerRef}
        className="relative w-full bg-gray-100 rounded-lg overflow-hidden"
        style={{ aspectRatio: `${viewerData.screen.trueWidth} / ${viewerData.screen.trueHeight}` }}
      >
        {(currentSlide.layouts ?? []).map((layout) => {
          const imageUrl = slideImages[layout.Key];
          return (
            <div
              key={layout.Key}
              className="absolute flex items-center justify-center"
              style={{
                left: `${layout.Left * 100}%`,
                top: `${layout.Top * 100}%`,
                width: `${tileSize.width * 100}%`,
                height: `${tileSize.height * 100}%`,
                transform: `rotate(${layout.rotation ?? layout.Rotation ?? 0}deg)`,
              }}
            >
              {imageUrl ? (
                <img src={imageUrl} alt="" className="w-full h-full object-contain" />
              ) : !imagesLoading ? (
                <span className="px-2 text-center text-xs text-gray-500">Image unavailable</span>
              ) : null}
            </div>
          );
        })}
        <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none" aria-label={`${mode} showing ${currentPoints.length} valid gaze points`} />
      </div>
    </div>
  );
}
